import { Data, Route } from "@angular/router";
import { FunnelHeaderMilestone } from "pax-ui/components";
import { UnreachableCaseError } from "ts-essentials";

import { Product } from "../../store/shared/product";

export enum Page {
  // Quote
  QUOTE_OPTIONAL_EMAIL = "optional-email",
  QUOTE_GENDER = `gender`,
  QUOTE_DATE_OF_BIRTH = "date-of-birth",
  QUOTE_SMOKER = "smoker",
  QUOTE_INSURED_SUM = "insured-sum",
  QUOTE_INSURANCE_TERM = "insurance-term",
  QUOTE_PREMIUM = "premium",
  QUOTE_EMAIL = "email",
  QUOTE_UNDERWRITING_BMI = "underwriting/bmi",
  QUOTE_UNDERWRITING_QUESTIONS = "underwriting/questions",
  QUOTE_BENEFICIARY = "beneficiary",
  QUOTE_PERSONAL_DETAILS = "personal-details",
  QUOTE_OCCUPATION = "occupation",
  QUOTE_SUMMARY = "summary",

  // Quote - Pillar 3a
  QUOTE_3A_PROJECTION = "projection",

  // Application
  APPLICATION_LIMITED_OFFER = "application/limited-offer",
  APPLICATION_PDF = "application/pdf",
  APPLICATION_UPLOAD_ID = "application/upload-id",
  APPLICATION_DIGITAL_SIGNATURE_AUTOMATIC_REDIRECT = "application/digital-signature/automatic-redirect",
  APPLICATION_DIGITAL_SIGNATURE_SUCCESS = "application/digital-signature/success",
  APPLICATION_DIGITAL_SIGNATURE_ERROR = `application/digital-signature/error`,
  APPLICATION_DIGITAL_SIGNATURE_DECLINED = "application/digital-signature/declined",
  APPLICATION_HAS_EXPIRED = "application/expired",

  // Top Level
  CONTINUE = "continue",
  TESTING = "testing",
  DEMO = "demo",
  DEFAULT = "",
}

type PageContextMap = { [key in Page]: PageContext };

export interface PageContext {
  feature: Feature;
}

export enum Feature {
  TOP_LEVEL = "top-level",
  QUOTE = "quote",
  APPLICATION = "application",
}

const quotePageContext: PageContext = { feature: Feature.QUOTE };
const applicationPageContext: PageContext = { feature: Feature.APPLICATION };
const topLevelPageContext: PageContext = { feature: Feature.TOP_LEVEL };

// This map should be product agnostic.
// Only store information which does not depend on the product and otherwise use the route to pass in product specific data.
export const pageContextMap: PageContextMap = {
  // Quote Pages
  [Page.QUOTE_OPTIONAL_EMAIL]: quotePageContext,
  [Page.QUOTE_GENDER]: quotePageContext,
  [Page.QUOTE_DATE_OF_BIRTH]: quotePageContext,
  [Page.QUOTE_SMOKER]: quotePageContext,
  [Page.QUOTE_INSURED_SUM]: quotePageContext,
  [Page.QUOTE_INSURANCE_TERM]: quotePageContext,
  [Page.QUOTE_PREMIUM]: quotePageContext,
  [Page.QUOTE_EMAIL]: quotePageContext,
  [Page.QUOTE_UNDERWRITING_BMI]: quotePageContext,
  [Page.QUOTE_UNDERWRITING_QUESTIONS]: quotePageContext,
  [Page.QUOTE_BENEFICIARY]: quotePageContext,
  [Page.QUOTE_PERSONAL_DETAILS]: quotePageContext,
  [Page.QUOTE_OCCUPATION]: quotePageContext,
  [Page.QUOTE_SUMMARY]: quotePageContext,

  // Quote pages - Pillar 3a
  [Page.QUOTE_3A_PROJECTION]: quotePageContext,

  // Application Pages
  [Page.APPLICATION_LIMITED_OFFER]: applicationPageContext,
  [Page.APPLICATION_PDF]: applicationPageContext,
  [Page.APPLICATION_UPLOAD_ID]: applicationPageContext,
  [Page.APPLICATION_DIGITAL_SIGNATURE_AUTOMATIC_REDIRECT]:
    applicationPageContext,
  [Page.APPLICATION_DIGITAL_SIGNATURE_SUCCESS]: applicationPageContext,
  [Page.APPLICATION_DIGITAL_SIGNATURE_ERROR]: applicationPageContext,
  [Page.APPLICATION_DIGITAL_SIGNATURE_DECLINED]: applicationPageContext,
  [Page.APPLICATION_HAS_EXPIRED]: applicationPageContext,

  // Top Level
  [Page.CONTINUE]: topLevelPageContext,
  [Page.TESTING]: topLevelPageContext,
  [Page.DEMO]: topLevelPageContext,
  [Page.DEFAULT]: topLevelPageContext,
};

export const START_PAGE = Page.QUOTE_OPTIONAL_EMAIL;

export enum QueryStringParameterKey {
  ///////////////////////////////////////////////////////////////
  // General
  ///////////////////////////////////////////////////////////////

  QUOTE_ID = "quoteId",
  APPLICATION_ID = "applicationId",
  ATTRIBUTION_CODE = "ac",

  ///////////////////////////////////////////////////////////////
  // Data Analytics
  ///////////////////////////////////////////////////////////////

  /**
   * In case of adding a new utm parameter, make sure to also update the {@link DataAnalyticsExtractorService.canAtLeastOneUtmParameterInUrlBeFound} method with the additional parameter.
   */
  UTM_SOURCE = "utm_source",
  UTM_MEDIUM = "utm_medium",
  UTM_CAMPAIGN = "utm_campaign",
  UTM_CONTENT = "utm_content",
  UTM_TERM = "utm_term",
  UTM_ID = "utm_id",
  GCL_ID = "gclid",
}

export function getPagePath(product: Product, page: Page) {
  switch (product) {
    case Product.TERM_LIFE_INSURANCE_CONSTANT:
      return `${ProductUrlPathSegment.TERM_LIFE_INSURANCE}/${page}`;
    case Product.PILLAR_3A_CONSTANT:
      return `${ProductUrlPathSegment.PILLAR_3A}/${page}`;
    default:
      throw new UnreachableCaseError(product);
  }
}

export enum ProductUrlPathSegment {
  TERM_LIFE_INSURANCE = "term-life-insurance",
  PILLAR_3A = "pillar-3a",
}

export interface CustomRoute extends Route {
  data: CustomRouteData;
}

export type CustomRouteData =
  | ProductAgnosticCustomRouteData
  | ProductSpecificCustomRouteData;

export interface NavigationRouteData {
  previousNavigationPage?: NavigationPage | undefined;
  nextNavigationPage?: NavigationPage | undefined;
}

export interface NavigationPage {
  pagePath: string;
  queryParamsHandling?: CustomQueryParamsHandling;
}

/**
 * A page which is not tied to a specific product.
 * @example /de/application/expired
 */
export interface ProductAgnosticCustomRouteData extends CustomRouteDataBase {
  type: Type.PRODUCT_AGNOSTIC;
}

/**
 * A page which can only be used in combination with a specific product.
 * @example: /de/term-life-insurance/application/limited-offer
 */
export interface ProductSpecificCustomRouteData extends CustomRouteDataBase {
  type: Type.PRODUCT_SPECIFIC;
  product: Product;

  navigation: NavigationRouteData;
  funnelHeaderMilestones?: FunnelHeaderMilestone[];
  dataAnalytics: DataAnalytics;
}

interface CustomRouteDataBase extends Data {
  type: Type;
  page: Page;

  /**
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/title
   */
  htmlTitle: string;

  /**
   * The most visible headline on the page.
   */
  pageTitle?: string;

  /**
   * The second most visible headline on the page. Usually displayed just below the {@link pageTitle}.
   */
  pageSubtitle?: string;
}

export enum Type {
  /**
   * Any page which contains a product as part of the url.
   * @example http://localhost:4200/de/term-life-insurance/premium
   */
  PRODUCT_SPECIFIC = "PRODUCT_SPECIFIC",

  /**
   * Any page which contains no product as part of the url and can be shared among different products.
   * @example http://localhost:4200/de/continue?quoteId=90488836-123b-4aee-990c-16c096bc7bfa
   * @example http://localhost:4200/de/application/expired?applicationId=90488836-123b-4aee-990c-16c096bc7bfa
   */
  PRODUCT_AGNOSTIC = "PRODUCT_AGNOSTIC",
}

export enum CustomQueryParamsHandling {
  // Only takes the query strings from the current url
  PRESERVE = "preserve",
  // Merges the provided query string keys with the one from the current url
  MERGE = "merge",
  // Replaces any of the query string keys from the current url with the ones being provided
  REPLACE = "",
}

/**
 * @see https://wiki.pax.ch/pages/viewpage.action?pageId=143296631
 * Section: Tracking Spezifikation
 */
export interface DataAnalytics {
  /**
   * The step number of the page within the funnel, starting with 1.
   * We use 'undefined' if the page is not part of the funnel.
   */
  funnelStepNumber: FunnelStepNumber | undefined;
}

export type FunnelStepNumber =
  // Term Life Insurance
  | TermLifeInsuranceSharedFunnelStepNumber
  | TermLifeInsuranceDigitalApplicationFunnelStepNumber
  | TermLifeInsuranceLegacyApplicationFunnelStepNumber
  // Pillar 3a
  | Pillar3aFunnelStepNumber;

/**
 * Term Life Insurance (Quote)
 */
export enum TermLifeInsuranceSharedFunnelStepNumber {
  QUOTE_OPTIONAL_EMAIL = 1,
  QUOTE_GENDER = 2,
  QUOTE_DATE_OF_BIRTH = 3,
  QUOTE_SMOKER = 4,
  QUOTE_INSURED_SUM = 5,
  QUOTE_INSURANCE_TERM = 6,
  QUOTE_PREMIUM = 7,
  QUOTE_EMAIL = 8,
  QUOTE_UNDERWRITING_BMI = 9,
  QUOTE_UNDERWRITING_QUESTIONS = 10,
  QUOTE_BENEFICIARY = 11,
  QUOTE_PERSONAL_DETAILS = 12,
  QUOTE_OCCUPATION = 13,
  QUOTE_SUMMARY = 14,
}

/**
 * Term Life Insurance (Digital Application)
 */
export enum TermLifeInsuranceDigitalApplicationFunnelStepNumber {
  APPLICATION_PDF = 15,
  APPLICATION_UPLOAD_ID = 16,
  APPLICATION_DIGITAL_SIGNATURE_SUCCESS = 17,
}

/**
 * Term Life Insurance (Legacy Application)
 */
export enum TermLifeInsuranceLegacyApplicationFunnelStepNumber {
  LIMITED_OFFER = 15,
}

// Specific to term life insurance. Concept should be extended with more products.
export const getFullHtmlTitle = (htmlTitle: string) =>
  $localize`Pax Todesfallversicherung` + " - " + htmlTitle;

export const getPillar3aFullHtmlTitle = (htmlTitle: string) =>
  $localize`Pax 3a` + " - " + htmlTitle;

export enum Pillar3aFunnelStepNumber {
  QUOTE_3A_PROJECTION = 1,
}
